import axios from '../../axios.js'
import {
  summarize,
  bucketByMonth,
} from '@/utils/peppolReportAggregator'

/**
 * IRSP (Singapore Peppol / InvoiceNow) Submissions Actions
 * 
 * API Endpoints Reference (Section 5.2 of IRSP Implementation Guide):
 * - GET /irsp/submissions - List submissions with pagination
 * - GET /irsp/submissions/:uuid - Get submission detail with documents and history
 * - POST /irsp/submissions/:uuid/refresh - Refresh submission status
 * - POST /irsp/submissions - Create new submission
 */

export default {
  /**
   * Fetch paginated list of IRSP submissions
   * 
   * @param {Object} payload - Query parameters
   * @param {number} payload.page - Page number (1-based)
   * @param {number} payload.limit - Items per page
   * @param {Object} payload.s - Search/filter parameters
   * @param {string} payload.sort - Sort parameter (e.g., "submissionDate,DESC")
   * @returns {Promise} Response with submissions array and pagination headers
   * 
   * API: GET /irsp/submissions?limit=50&offset=0&...filters
   */
  fetchRecords({ commit }, payload = {}) {
    return new Promise((resolve, reject) => {
      // Convert page-based pagination to offset-based as per API spec
      const { page = 1, limit = 25, ...rest } = payload
      const offset = (page - 1) * limit

      const params = {
        limit,
        offset,
        ...rest,
      }

      axios
        .get('/irsp-submissions', { params })
        .then((response) => {
          const submissions = response.data?.data || response.data || []
          commit('SET_SUBMISSIONS', submissions)
          
          // Handle pagination from response headers or body
          const total = parseInt(response.headers['x-pagination-total']) || 
                       response.data?.total || 
                       submissions.length
          const pageCount = Math.ceil(total / limit) || 1
          
          commit('SET_PAGINATION', {
            currentPage: page,
            totalPage: pageCount,
            pageTotal: parseInt(response.headers['x-pagination-count']) || submissions.length,
            totalItem: total,
            limit,
            offset,
          })
          resolve(response)
        })
        .catch((error) => {
          console.error('[IRSP] Failed to fetch submissions:', error)
          reject(error)
        })
    })
  },

  /**
   * Fetch single submission detail by UUID
   * Returns submission with documents and history in a single call
   * 
   * @param {Object} context - Vuex context
   * @param {string} uuid - Submission UUID
   * @returns {Promise} Response containing { submission, documents, history }
   * 
   * API: GET /irsp/submissions/:uuid
   */
  fetchRecord(context, uuid) {
    return new Promise((resolve, reject) => {
      if (!uuid) {
        reject(new Error('Submission UUID is required'))
        return
      }

      axios
        .get(`/irsp-submissions/${uuid}`)
        .then((response) => {
          // API returns { submission, documents, history } per spec
          resolve(response)
        })
        .catch((error) => {
          console.error(`[IRSP] Failed to fetch submission ${uuid}:`, error)
          reject(error)
        })
    })
  },

  /**
   * Fetch submission documents separately (fallback if not included in detail)
   * 
   * @param {Object} context - Vuex context
   * @param {string} uuid - Submission UUID
   * @returns {Promise} Response with documents array
   * 
   * API: GET /irsp/submissions/:uuid (documents field from response)
   */
  fetchDocuments(context, uuid) {
    return new Promise((resolve, reject) => {
      if (!uuid) {
        reject(new Error('Submission UUID is required'))
        return
      }

      axios
        .get(`/irsp-submissions/${uuid}?join=documents`)
        .then((response) => {
          // Extract documents from full response
          const documents = response.data?.documents || []
          resolve({ data: documents })
        })
        .catch((error) => {
          console.error(`[IRSP] Failed to fetch documents for ${uuid}:`, error)
          reject(error)
        })
    })
  },

  /**
   * Fetch submission history separately (fallback if not included in detail)
   * 
   * @param {Object} context - Vuex context
   * @param {string} uuid - Submission UUID
   * @returns {Promise} Response with history array
   * 
   * API: GET /irsp/submissions/:uuid (history field from response)
   */
  fetchHistory(context, uuid) {
    return new Promise((resolve, reject) => {
      if (!uuid) {
        reject(new Error('Submission UUID is required'))
        return
      }

      axios
        .get(`/irsp-submissions/${uuid}?join=history`)
        .then((response) => {
          // Extract history from full response
          const history = response.data?.history || []
          resolve({ data: history })
        })
        .catch((error) => {
          console.error(`[IRSP] Failed to fetch history for ${uuid}:`, error)
          reject(error)
        })
    })
  },

  /**
   * Fetch complete submission detail with documents and history
   * Optimized single call to get all related data
   * 
   * @param {Object} context - Vuex context
   * @param {string} uuid - Submission UUID
   * @returns {Promise} Response containing { submission, documents, history }
   * 
   * API: GET /irsp/submissions/:uuid
   */
  fetchSubmissionDetail(context, uuid) {
    return new Promise((resolve, reject) => {
      if (!uuid) {
        reject(new Error('Submission UUID is required'))
        return
      }

      // documents/history are eager:false on the CRUD entity, so they must be
      // explicitly requested via join params or the detail comes back empty.
      axios
        .get(`/irsp-submissions/${uuid}?join=documents&join=history`)
        .then((response) => {
          // Normalize response structure
          const data = response.data
          const result = {
            submission: data.submission || data,
            documents: data.documents || [],
            history: data.history || [],
          }
          resolve({ data: result })
        })
        .catch((error) => {
          console.error(`[IRSP] Failed to fetch submission detail ${uuid}:`, error)
          reject(error)
        })
    })
  },

  /**
   * Refresh submission status from IRAS
   * Triggers a status check with IRAS and updates the submission
   * 
   * @param {Object} context - Vuex context
   * @param {string} uuid - Submission UUID
   * @returns {Promise} Response with updated submission data
   * 
   * API: POST /irsp/submissions/:uuid/refresh
   */
  refreshStatus(context, uuid) {
    return new Promise((resolve, reject) => {
      if (!uuid) {
        reject(new Error('Submission UUID is required'))
        return
      }

      axios
        .post(`/irsp-submissions/${uuid}/refresh`)
        .then((response) => {
          // API returns { submission, refreshed: true }
          resolve(response)
        })
        .catch((error) => {
          console.error(`[IRSP] Failed to refresh status for ${uuid}:`, error)
          reject(error)
        })
    })
  },

  /**
   * Create a new IRSP submission
   * Submits documents to IRAS via InvoiceNow
   * 
   * @param {Object} context - Vuex context
   * @param {Object} payload - Submission payload
   * @param {Array<{docNo: string, type: string}>} payload.billingDocs - Array of billing docs with docNo and type
   * @param {string} [payload.destination] - Optional: 'C3C5' (Peppol+IRAS) or 'C5' (IRAS only)
   * @returns {Promise} Response with created submission
   * 
   * API: POST /irsp-submissions
   */
  createSubmission(context, payload) {
    return new Promise((resolve, reject) => {
      if (!payload || !payload.billingDocs || payload.billingDocs.length === 0) {
        reject(new Error('At least one billing document is required'))
        return
      }

      const requestBody = {
        billingDocs: payload.billingDocs || [],
      }

      // Only include destination if explicitly provided
      if (payload.destination) {
        requestBody.destination = payload.destination
      }

      axios
        .post('/irsp-submissions', requestBody)
        .then((response) => {
          // API returns { uuid, transactionId, statusOverall, docCount }
          resolve(response)
        })
        .catch((error) => {
          console.error('[IRSP] Failed to create submission:', error)
          reject(error)
        })
    })
  },

  /**
   * Submit multiple documents to IRSP
   * Convenience method that wraps createSubmission
   * 
   * @param {Object} context - Vuex context
   * @param {Object} payload - Submission payload
   * @param {string[]} payload.docNos - Array of document numbers
   * @param {string} [payload.destination] - Optional destination override
   * @returns {Promise} Response with created submission
   */
  submitDocuments(context, payload) {
    return context.dispatch('createSubmission', payload)
  },

  /**
   * Check a GST registration number against IRAS' GST Register via api-core.
   * API: GET /irsp-submissions/check-gst/:regId
   */
  checkGstRegistration(context, payload = {}) {
    const regId = payload.regId
    const params = {}
    if (payload.invoiceDate) {
      params.invoiceDate = payload.invoiceDate
    }

    return axios
      .get(`/irsp-submissions/check-gst/${encodeURIComponent(regId)}`, { params })
      .then((r) => r.data)
  },

  // ---------------------------------------------------------------------------
  // GST InvoiceNow (C5) Activation — SFAS SMP "C5 Activation" API
  // (PRSP & IRSP Integration Document v1.1, §10). Backed by /irsp-c5-activation/*
  // which reuses the IRSP_SUBMISSIONS resource/permissions.
  // ---------------------------------------------------------------------------

  /**
   * Fetch the current tenant's C5 activation status (re-synced from the SMP).
   * API: GET /irsp-c5-activation/status
   * @returns {Promise<{status, initiatedAt?, activatedAt?, representativeName?, representativeEmail?}>}
   */
  fetchC5Status() {
    return axios.get('/irsp-c5-activation/status').then((r) => r.data)
  },

  /**
   * Activate GST InvoiceNow (C5) for the current tenant. Supply EITHER the
   * Email Flow (representativeName + representativeEmail) OR the Redirect Flow
   * (successfulRedirectUrl + unsuccessfulRedirectUrl).
   * API: POST /irsp-c5-activation/activate
   * @returns {Promise<{participantId, action, flow, redirectUrl?, status}>}
   */
  activateC5(context, payload = {}) {
    return axios.post('/irsp-c5-activation/activate', payload).then((r) => r.data)
  },

  /**
   * Deactivate GST InvoiceNow (C5) for the current tenant.
   * API: DELETE /irsp-c5-activation/deactivate
   */
  deactivateC5() {
    return axios.delete('/irsp-c5-activation/deactivate').then((r) => r.data)
  },

  /** Cancel an in-progress activation. API: DELETE /irsp-c5-activation/cancel-activation */
  cancelC5Activation() {
    return axios.delete('/irsp-c5-activation/cancel-activation').then((r) => r.data)
  },

  /** Cancel an in-progress deactivation. API: DELETE /irsp-c5-activation/cancel-deactivation */
  cancelC5Deactivation() {
    return axios.delete('/irsp-c5-activation/cancel-deactivation').then((r) => r.data)
  },

  // ---------------------------------------------------------------------------
  // Sending Peppol responses (Sending of Order Response / Invoice Response).
  // The recipient and referenced original document are derived server-side from
  // the stored inbound record identified by `inboundId`, so the client only
  // supplies the document type, inbound id and the coded response status.
  // API: POST /irsp-submissions/response
  // ---------------------------------------------------------------------------

  /**
   * Send a BIS Order/Invoice response for a received document.
   * @param {Object} payload
   * @param {'OrderResponse'|'InvoiceResponse'} payload.documentType
   * @param {string} payload.inboundId - peppol_inbound_order id (OrderResponse)
   *   or peppol_ap_document id (InvoiceResponse)
   * @param {string} payload.responseCode - BIS code valid for the documentType
   * @param {string} [payload.note] - optional free-text note (audit only)
   * @returns {Promise<Object>} the created IRSP submission
   */
  sendResponse(context, payload = {}) {
    return axios.post('/irsp-submissions/response', payload).then((r) => r.data)
  },

  /**
   * Bulk refresh multiple submission statuses
   * 
   * @param {Object} context - Vuex context
   * @param {string[]} uuids - Array of submission UUIDs to refresh
   * @returns {Promise} Array of results
   */
  async bulkRefreshStatus(context, uuids) {
    if (!uuids || uuids.length === 0) {
      return []
    }

    const results = await Promise.allSettled(
      uuids.map((uuid) => context.dispatch('refreshStatus', uuid))
    )

    return results.map((result, index) => ({
      uuid: uuids[index],
      success: result.status === 'fulfilled',
      data: result.status === 'fulfilled' ? result.value?.data : null,
      error: result.status === 'rejected' ? result.reason?.message : null,
    }))
  },

  /**
   * Fetch ALL submissions in a date range for client-side reporting.
   *
   * Pages through GET /irsp-submissions using the same offset pagination and
   * server-side submissionDate filtering as the list view, accumulating every
   * matching submission so the pure aggregator helpers can derive figures.
   *
   * @param {Object} context - Vuex context
   * @param {Object} payload
   * @param {string} [payload.from] - ISO start of range (inclusive)
   * @param {string} [payload.to] - ISO end of range (inclusive)
   * @param {number} [payload.limit=100] - Page size for fetching
   * @param {Array}  [payload.search] - Extra CRUD-style filter conditions
   * @returns {Promise<Array>} All matching submissions
   */
  async fetchAllForReport(context, payload = {}) {
    const { from, to, limit = 100, search } = payload

    const conditions = []
    if (from) conditions.push({ submissionDate: { $gte: from } })
    if (to) conditions.push({ submissionDate: { $lte: to } })
    if (Array.isArray(search)) conditions.push(...search)
    const s = conditions.length ? { $and: conditions } : undefined

    const all = []
    let offset = 0
    // Hard cap to avoid unbounded loops on unexpected pagination metadata.
    const maxPages = 1000

    for (let page = 0; page < maxPages; page++) {
      const params = { limit, offset }
      if (s) params.s = s

      const response = await axios.get('/irsp-submissions', { params })
      const batch = response.data?.data || response.data || []
      const list = Array.isArray(batch) ? batch : []
      all.push(...list)

      const total =
        parseInt(response.headers['x-pagination-total'], 10) ||
        response.data?.total ||
        0

      offset += limit
      if (list.length < limit) break
      if (total && offset >= total) break
    }

    return all
  },

  /**
   * Aggregated Peppol counts for the dashboard.
   *
   * Tries a backend aggregation endpoint first and transparently falls back to
   * client-side aggregation when it is unavailable (e.g. not yet implemented).
   *
   * @param {Object} context - Vuex context
   * @param {Object} payload - { from, to, ...filters }
   * @returns {Promise<Object>} summarize() shape
   */
  async fetchPeppolCount(context, payload = {}) {
    try {
      const response = await axios.get('/irsp-submissions/count', {
        params: payload,
      })
      return response.data
    } catch (error) {
      const submissions = await context.dispatch('fetchAllForReport', payload)
      return summarize(submissions)
    }
  },

  /**
   * Monthly Peppol reporting figures (Peppol Authority compliance table).
   *
   * Tries a backend aggregation endpoint first and transparently falls back to
   * client-side aggregation when it is unavailable.
   *
   * @param {Object} context - Vuex context
   * @param {Object} payload - { from, to, ...filters }
   * @returns {Promise<Array>} bucketByMonth() rows
   */
  async fetchPeppolMonthlySummary(context, payload = {}) {
    try {
      const response = await axios.get('/irsp-submissions/monthly-summary', {
        params: payload,
      })
      return response.data
    } catch (error) {
      const submissions = await context.dispatch('fetchAllForReport', payload)
      return bucketByMonth(submissions, { from: payload.from, to: payload.to })
    }
  },

  /**
   * Clear submissions from store
   * Useful for cleanup when navigating away
   * 
   * @param {Object} context - Vuex context
   */
  clearSubmissions({ commit }) {
    commit('SET_SUBMISSIONS', [])
    commit('SET_PAGINATION', {
      currentPage: 1,
      totalPage: 0,
      pageTotal: 0,
      totalItem: 0,
      limit: 25,
      offset: 0,
    })
  },
}
